import { SuccessfulAddSiteDialog } from '../components/sites/successfulAddSiteDialog';
import SitesPageHeader from '../components/sites/sitesPageHeader';
import { AddSiteDialog } from '../components/sites/addSiteDialog';
import { useState, useCallback, useEffect } from 'react';
import SiteFiltration from '../components/sites/siteFiltration';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { Divider, Box, LinearProgress } from '@mui/material';
import { useHasPermission } from '../hooks/useHasPermission';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import ListView from '../components/sites/ListView';
import GridView from '../components/sites/GridView';
import {
    ListNavigationProvider,
    useListPageNavigation,
    usePersistedListParams,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';

const Sites = () => {

    const hasSitesCreatePermission = useHasPermission('site_create');

    const [siteId, setSiteId] = useState(null);
    const [openAddSite, setOpenAddSite] = useState(false);
    const [openSuccessfulAddSite, setOpenSuccessfulAddSite] = useState(false);

    const [loading, setLoading] = useState(true);
    const [sites, setSites] = useState([]);
    const [totalSites, setTotalSites] = useState('');
    const [sitesFetched, setSitesFetched] = useState(0);
    const [filteredSitesCount, setFilteredSitesCount] = useState(0);
    const defaultSortBy = {
        name: null,
        address: null,
        postal_code: null,
        city: null,
        region: null,
        phone_number: null,
    };
    const {
        filters: siteFilters,
        setFilters: setSiteFilters,
        sortBy,
        setSortBy,
        displayMode: selectedDisplayMode,
        setDisplayMode: setSelectedDisplayMode,
    } = usePersistedListParams({
        keys: {
            filters: 'siteFilters',
            sortBy: 'siteSortBy',
            displayMode: 'selectedSitesDisplayMode',
        },
        defaults: {
            filters: { debouncedSearch: '' },
            sortBy: defaultSortBy,
            displayMode: 'grid',
        },
    });
    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'sites',
        pathname: '/dashboard/sites',
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 350, list: 70 },
        resetOnFilters: siteFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(siteFilters);

    useEffect(() => {
        setLoading(true)
    }, [siteFilters]);


    const fetchSites = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('site/list', { params: { offset: adjustedOffset, limit, filters: siteFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedSites = [];
                // Update the fetched records in their correct positions
                response?.data?.sites?.forEach((site, index) => updatedSites[adjustedOffset + index] = site);
                // Update the state with the new files array
                setSites(updatedSites);
                setFilteredSitesCount(response?.data?.filteredSitesCount);
                setTotalSites(response?.data?.totalSites);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching sites:', error);
            CustomErrorLogger(error, 'error fetch sites in sites page');
        }
    }, [siteFilters, offset, sortBy]);

    useEffect(() => {
        fetchSites()
    }, [fetchSites]);

    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Site" list={true}>

            <SitesPageHeader setOpenAddSite={setOpenAddSite} />
            <Divider />
            {loading ?
                <Box>
                    <SiteFiltration
                        filtersEmpty={filtersEmpty}
                        siteFilters={siteFilters}
                        setSiteFilters={setSiteFilters}
                        totalSites={totalSites}
                        filteredSitesCount={filteredSitesCount}
                        sitesFetched={sitesFetched}
                        selectedDisplayMode={selectedDisplayMode}
                        setSelectedDisplayMode={setSelectedDisplayMode}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box>
                :
                totalSites === 0 ?
                    <NoRecord
                        description="Aucun site ajouté pour le moment"
                        buttonTitle="Ajouter un site"
                        handleOpenDialog={() => setOpenAddSite(true)}
                        hasPermission={hasSitesCreatePermission}
                    /> :
                    <Box>
                        <SiteFiltration
                            filtersEmpty={filtersEmpty}
                            siteFilters={siteFilters}
                            setSiteFilters={setSiteFilters}
                            totalSites={totalSites}
                            filteredSitesCount={filteredSitesCount}
                            sitesFetched={sitesFetched}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                        />

                        {selectedDisplayMode === 'grid' ? (
                            <GridView
                                scrollRef={scrollRef}
                                sites={sites}
                                setOffset={setOffset}
                                filteredSitesCount={filteredSitesCount}
                                setSitesFetched={setSitesFetched}
                            />
                        ) : (
                            <ListView
                                scrollRef={scrollRef}
                                sites={sites}
                                setOffset={setOffset}
                                sortBy={sortBy}
                                filteredSitesCount={filteredSitesCount}
                                setSitesFetched={setSitesFetched}
                                setSortBy={setSortBy}
                            />
                        )}

                    </Box>
            }

            {openAddSite &&
                <AddSiteDialog
                    setSites={setSites}
                    setTotalSites={setTotalSites}
                    setFilteredSitesCount={setFilteredSitesCount}
                    openAddSite={openAddSite}
                    setOpenAddSite={setOpenAddSite}
                    setSiteId={setSiteId}
                />
            }

            {openSuccessfulAddSite &&
                <SuccessfulAddSiteDialog
                    openSuccessfulAddSite={openSuccessfulAddSite}
                    siteId={siteId}
                    setOpenAddSite={setOpenAddSite}
                />
            }

        </Page>
        </ListNavigationProvider>
    );
};

export default Sites;